/**
 * src/services/exportService.ts
 *
 * Streaming ZIP package export engine and R2 payload bundler (Milestone 5).
 * Uses client-zip and WHATWG Streams / AsyncGenerators for < 2 MB RAM footprint.
 */

import { makeZip } from 'client-zip';
import { PolarstepsTrip } from '../types/polarsteps';
import { PhotoRecord } from '../types/domain';
import { StorageService, storageService as defaultStorageService } from './storage';
import {
  generateStandaloneViewerHtml,
  getStandaloneViewerJs,
  getStandaloneViewerCss,
} from './standaloneViewerAssets';

export interface ExportBundleManifest {
  version: '1.0';
  generatedAt: string;
  generator: string;
  trip: {
    id: number | string;
    name: string;
    summary?: string;
    start_date: number;
    end_date: number;
    timezone_id: string;
    total_km?: number;
    step_count: number;
    location_count: number;
  };
  stats: {
    totalPhotos: number;
    categories: string[];
    totalThumbnailBytes: number;
    totalPreviewBytes: number;
    totalPackageBytes: number;
    matchingBreakdown: {
      exact: number;
      interpolated: number;
      snap_fallback: number;
      step_fallback: number;
      unmatched: number;
    };
  };
  files: {
    trip: string;
    photos: string;
    manifest: string;
    dataJs: string;
    routeGeoJson?: string;
    thumbnailsDir: string;
    previewsDir: string;
  };
}

export interface ExportProgress {
  percent: number;           // 0 to 100
  currentFileName: string;   // e.g. "photos/thumb/p123.webp"
  filesProcessed: number;    // e.g. 450
  totalFiles: number;        // e.g. 3004
  bytesProcessed: number;    // Bytes passed into stream
  totalBytes: number;        // Total estimated archive bytes
}

export type ExportProgressCallback = (
  percent: number,
  currentFileName: string,
  details?: ExportProgress
) => void;

export interface ExportPackageOptions {
  suggestedFileName?: string;
  onProgress?: ExportProgressCallback;
  storageService?: StorageService;
  includeHdPreviews?: boolean; // defaults to true; if false, only thumbnails
  signal?: AbortSignal;
}

export interface ExportResult {
  fileName: string;
  method: 'file_system_api' | 'blob_download';
  totalBytes: number;
  fileCount: number;
  durationMs: number;
}

export interface ZipEntryDescriptor {
  name: string;
  input: Blob | string | Uint8Array | ReadableStream<Uint8Array>;
  lastModified?: Date;
  size?: number | bigint;
}

/**
 * Generator that lazily queries StorageService for one photo blob at a time,
 * emitting zip entry descriptors without accumulating files in RAM.
 */
export async function* createExportStreamGenerator(
  trip: PolarstepsTrip,
  photos: PhotoRecord[],
  storage: StorageService,
  options: {
    manifest: ExportBundleManifest;
    dataJsContent: string;
    routeGeoJsonContent?: string;
    includeHdPreviews?: boolean;
    onProgress?: ExportProgressCallback;
    signal?: AbortSignal;
    totalEstimatedBytes: number;
    totalFiles: number;
  }
): AsyncGenerator<ZipEntryDescriptor, void, unknown> {
  const {
    manifest,
    dataJsContent,
    routeGeoJsonContent,
    includeHdPreviews = true,
    onProgress,
    signal,
    totalEstimatedBytes,
    totalFiles,
  } = options;

  let processedBytes = 0;
  let filesProcessed = 0;

  const emitProgress = (fileName: string, addedBytes: number) => {
    processedBytes += addedBytes;
    filesProcessed += 1;
    const percent = Math.min(
      99,
      Math.round((processedBytes / Math.max(1, totalEstimatedBytes)) * 100)
    );
    if (onProgress) {
      onProgress(percent, fileName, {
        percent,
        currentFileName: fileName,
        filesProcessed,
        totalFiles,
        bytesProcessed: processedBytes,
        totalBytes: totalEstimatedBytes,
      });
    }
  };

  const checkAbort = () => {
    if (signal?.aborted) {
      throw new DOMException('AbortError: Export aborted by user', 'AbortError');
    }
  };


  // 1. Root & Viewer Assets
  const indexHtml = generateStandaloneViewerHtml(trip.name || 'Travel Photo Map');
  const viewerJs = getStandaloneViewerJs();
  const viewerCss = getStandaloneViewerCss();

  const rootFiles: Array<{ name: string; content: string }> = [
    { name: 'index.html', content: indexHtml },
    { name: 'viewer.js', content: viewerJs },
    { name: 'viewer.css', content: viewerCss },
    { name: 'data.js', content: dataJsContent },
    { name: 'manifest.json', content: JSON.stringify(manifest, null, 2) },
    { name: 'trip.json', content: JSON.stringify(trip, null, 2) },
    { name: 'photos.json', content: JSON.stringify(photos, null, 2) },
  ];

  if (routeGeoJsonContent) {
    rootFiles.push({ name: 'data/route.geojson', content: routeGeoJsonContent });
  }

  const textEncoder = new TextEncoder();

  for (const item of rootFiles) {
    checkAbort();
    const encoded = textEncoder.encode(item.content);
    emitProgress(item.name, encoded.byteLength);
    yield {
      name: item.name,
      input: encoded,
      size: encoded.byteLength,
      lastModified: new Date(trip.end_date ? trip.end_date * 1000 : Date.now()),
    };
  }

  // 2. Photo Binaries Stream (Thumbnails + Previews)
  for (let i = 0; i < photos.length; i++) {
    checkAbort();
    const photo = photos[i];
    const photoDate = photo.timestampUtc
      ? new Date(photo.timestampUtc * 1000)
      : new Date();

    // 2a. Map Thumbnail (400x400)
    const thumbKey = photo.thumbnailKey || `thumb_${photo.id}.webp`;
    const thumbName = `photos/thumb/${photo.id}.webp`;

    const thumbBlob = await storage.getPhotoBlob(thumbKey);
    if (thumbBlob) {
      emitProgress(thumbName, thumbBlob.size);
      yield {
        name: thumbName,
        input: thumbBlob,
        size: thumbBlob.size,
        lastModified: photoDate,
      };
    }

    // 2b. Fullscreen HD Preview (1600px)
    if (includeHdPreviews) {
      checkAbort();
      const prevKey = photo.previewKey || `prev_${photo.id}.webp`;
      const prevName = `photos/hd/${photo.id}.webp`;

      const prevBlob = await storage.getPhotoBlob(prevKey);
      if (prevBlob) {
        emitProgress(prevName, prevBlob.size);
        yield {
          name: prevName,
          input: prevBlob,
          size: prevBlob.size,
          lastModified: photoDate,
        };
      }
    }
  }

  // Final 100% notification
  if (onProgress) {
    onProgress(100, 'Finalizing ZIP package...', {
      percent: 100,
      currentFileName: 'Finalizing ZIP package...',
      filesProcessed: totalFiles,
      totalFiles,
      bytesProcessed: totalEstimatedBytes,
      totalBytes: totalEstimatedBytes,
    });
  }
}

/**
 * Service class orchestrating streaming ZIP export and Cloudflare R2 bundle preparation.
 */
export class ExportEngineService {
  private storage: StorageService;

  constructor(storageService: StorageService = defaultStorageService) {
    this.storage = storageService;
  }

  /**
   * Main entry point: automatically selects the most memory-efficient
   * sink available in the host browser.
   */
  async exportTripPackage(
    tripId?: number | string,
    options: ExportPackageOptions = {}
  ): Promise<ExportResult> {
    const startTime = performance.now();
    const storage = options.storageService || this.storage;
    await storage.init();

    // 1. Resolve Trip & Photos
    let trip: PolarstepsTrip | undefined;
    if (tripId) {
      trip = await storage.getTrip(tripId);
    } else {
      const trips = await storage.getAllTrips();
      trip = trips[0];
    }

    if (!trip) {
      throw new Error('No trip available to export.');
    }

    const photos = await storage.getPhotos();
    if (photos.length === 0) {
      throw new Error('No photos available to export for this trip.');
    }

    // 2. Prepare Metadata, Manifest, and Pre-calculated Route
    const tripSlug = (trip.name || 'trip')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '_')
      .replace(/_+/g, '_');
    const defaultFileName = `${tripSlug}_travel_package.zip`;
    const finalFileName = options.suggestedFileName || defaultFileName;

    const includeHd = options.includeHdPreviews ?? true;
    const manifest = this.buildManifest(trip, photos, includeHd);
    const dataJsContent = this.buildDataJs(trip, photos, manifest);
    const routeGeoJsonContent = this.buildRouteGeoJson(trip);

    const totalEstimatedBytes = manifest.stats.totalPackageBytes;
    const totalFiles = (includeHd ? photos.length * 2 : photos.length) + 8; // root files

    // 3. Instantiate the Lazy Generator
    const generator = createExportStreamGenerator(trip, photos, storage, {
      manifest,
      dataJsContent,
      routeGeoJsonContent,
      includeHdPreviews: includeHd,
      onProgress: options.onProgress,
      signal: options.signal,
      totalEstimatedBytes,
      totalFiles,
    });

    // 4. Create client-zip stream
    const zipReadableStream = makeZip(generator, {
      buffersAreUTF8: true,
    });

    // 5. Select Stream Sink
    let method: 'file_system_api' | 'blob_download' = 'blob_download';

    if (typeof window !== 'undefined' && typeof (window as any).showSaveFilePicker === 'function') {
      try {
        const fileHandle = await (window as any).showSaveFilePicker({
          suggestedName: finalFileName,
          types: [
            {
              description: 'ZIP Archive (*.zip)',
              accept: { 'application/zip': ['.zip'] },
            },
          ],
        });

        const writable: FileSystemWritableFileStream = await fileHandle.createWritable();

        // Direct stream pipe to disk with backpressure
        await zipReadableStream.pipeTo(writable);
        method = 'file_system_api';
      } catch (err: any) {
        // If user cancelled the picker dialog, do not trigger fallback download
        if (err.name === 'AbortError') {
          throw err;
        }
        console.warn('FileSystemAccess write failed, falling back to blob download', err);
        await this.fallbackBlobDownload(zipReadableStream, finalFileName);
        method = 'blob_download';
      }
    } else {
      // Universal fallback for Safari / Firefox / mobile
      await this.fallbackBlobDownload(zipReadableStream, finalFileName);
      method = 'blob_download';
    }

    return {
      fileName: finalFileName,
      method,
      totalBytes: totalEstimatedBytes,
      fileCount: totalFiles,
      durationMs: Math.round(performance.now() - startTime),
    };
  }

  /**
   * Universal streaming fallback using Response.blob()
   */
  async fallbackBlobDownload(
    stream: ReadableStream<Uint8Array>,
    fileName: string
  ): Promise<void> {
    const response = new Response(stream, {
      headers: {
        'Content-Type': 'application/zip',
        'Content-Disposition': `attachment; filename="${fileName}"`,
      },
    });

    const blob = await response.blob();

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  }

  /**
   * Prepares individual files and manifest for Cloudflare R2 bucket upload.
   */
  async prepareR2Payload(
    trip?: PolarstepsTrip,
    photos?: PhotoRecord[],
    options: { includeHdPreviews?: boolean } = {}
  ): Promise<{ files: Map<string, Blob>; manifest: ExportBundleManifest }> {
    await this.storage.init();

    const resolvedTrip = trip || (await this.storage.getAllTrips())[0];
    if (!resolvedTrip) {
      throw new Error('No trip available for R2 payload preparation.');
    }

    const resolvedPhotos = photos || (await this.storage.getPhotos());
    const includeHd = options.includeHdPreviews ?? true;
    const manifest = this.buildManifest(resolvedTrip, resolvedPhotos, includeHd);

    const files = new Map<string, Blob>();

    // Manifest, Trip, Photos, Route
    files.set(
      'manifest.json',
      new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' })
    );
    files.set(
      'trip.json',
      new Blob([JSON.stringify(resolvedTrip, null, 2)], { type: 'application/json' })
    );
    files.set(
      'photos.json',
      new Blob([JSON.stringify(resolvedPhotos, null, 2)], { type: 'application/json' })
    );
    files.set(
      'data/route.geojson',
      new Blob([this.buildRouteGeoJson(resolvedTrip)], { type: 'application/geo+json' })
    );

    // Photos binaries
    for (const p of resolvedPhotos) {
      const thumbKey = p.thumbnailKey || `thumb_${p.id}.webp`;
      const thumbBlob = await this.storage.getPhotoBlob(thumbKey);
      if (thumbBlob) {
        files.set(`photos/thumb/${p.id}.webp`, thumbBlob);
      }

      if (includeHd) {
        const prevKey = p.previewKey || `prev_${p.id}.webp`;
        const prevBlob = await this.storage.getPhotoBlob(prevKey);
        if (prevBlob) {
          files.set(`photos/hd/${p.id}.webp`, prevBlob);
        }
      }
    }

    return { files, manifest };
  }

  /**
   * Helper: Builds canonical manifest
   */
  buildManifest(
    trip: PolarstepsTrip,
    photos: PhotoRecord[],
    includeHd = true
  ): ExportBundleManifest {
    const categories = Array.from(
      new Set(photos.map((p) => p.category).filter(Boolean))
    ).sort();

    const matchingBreakdown = {
      exact: 0,
      interpolated: 0,
      snap_fallback: 0,
      step_fallback: 0,
      unmatched: 0,
    };

    let totalThumbBytes = 0;
    let totalPrevBytes = 0;

    for (const p of photos) {
      if (p.matchingConfidence in matchingBreakdown) {
        matchingBreakdown[p.matchingConfidence as keyof typeof matchingBreakdown]++;
      }
      totalThumbBytes += p.thumbnailSize || 50000;
      if (includeHd) {
        totalPrevBytes += p.previewSize || 180000;
      }
    }

    const metadataBytes = 500000; // estimated ~500 KB for JSON + HTML + JS
    const totalPackageBytes = totalThumbBytes + totalPrevBytes + metadataBytes;

    return {
      version: '1.0',
      generatedAt: new Date().toISOString(),
      generator: 'Travel Photo Map PWA v1.0.0 (Milestone 5)',
      trip: {
        id: trip.id,
        name: trip.name,
        summary: trip.summary,
        start_date: trip.start_date,
        end_date: trip.end_date,
        timezone_id: trip.timezone_id || 'UTC',
        total_km: trip.total_km,
        step_count: trip.all_steps?.length || 0,
        location_count: trip.locations?.length || 0,
      },
      stats: {
        totalPhotos: photos.length,
        categories,
        totalThumbnailBytes: totalThumbBytes,
        totalPreviewBytes: totalPrevBytes,
        totalPackageBytes,
        matchingBreakdown,
      },
      files: {
        trip: 'trip.json',
        photos: 'photos.json',
        manifest: 'manifest.json',
        dataJs: 'data.js',
        routeGeoJson: 'data/route.geojson',
        thumbnailsDir: 'photos/thumb/',
        previewsDir: 'photos/hd/',
      },
    };
  }

  /**
   * Builds CORS-free data.js script file
   */
  buildDataJs(
    trip: PolarstepsTrip,
    photos: PhotoRecord[],
    manifest: ExportBundleManifest
  ): string {
    const payload = {
      manifest,
      trip,
      photos: photos.map((p) => ({
        ...p,
        thumbnailUrl: `photos/thumb/${p.id}.webp`,
        previewUrl: `photos/hd/${p.id}.webp`,
      })),
    };

    return `/**
 * Standalone Offline Travel Data Store
 * Enables opening index.html via local file:// without browser CORS restrictions.
 */
window.__TRAVEL_DATA__ = ${JSON.stringify(payload, null, 2)};
`;
  }

  /**
   * Pre-computes route GeoJSON line with anti-meridian handling
   */
  buildRouteGeoJson(trip: PolarstepsTrip): string {
    const coordinates = (trip.locations || []).map((loc) => [loc.lon, loc.lat]);
    const geojson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            name: trip.name,
            total_km: trip.total_km,
          },
          geometry: {
            type: 'LineString',
            coordinates,
          },
        },
      ],
    };
    return JSON.stringify(geojson, null, 2);
  }
}

/**
 * Top-level convenience functions
 */

export function generateZipPackage(
  trip: PolarstepsTrip,
  photos: PhotoRecord[],
  storage: StorageService,
  options: {
    manifest: ExportBundleManifest;
    dataJsContent: string;
    routeGeoJsonContent?: string;
    includeHdPreviews?: boolean;
    onProgress?: ExportProgressCallback;
    signal?: AbortSignal;
    totalEstimatedBytes: number;
    totalFiles: number;
  }
): ReadableStream<Uint8Array> {
  const generator = createExportStreamGenerator(trip, photos, storage, options);
  return makeZip(generator, { buffersAreUTF8: true });
}

export async function streamZipExport(
  tripId?: number | string,
  options: ExportPackageOptions = {}
): Promise<ExportResult> {
  const service = new ExportEngineService(options.storageService || defaultStorageService);
  return service.exportTripPackage(tripId, options);
}

export async function prepareR2Payload(
  trip?: PolarstepsTrip,
  photos?: PhotoRecord[],
  storage?: StorageService,
  options: { includeHdPreviews?: boolean } = {}
): Promise<{ files: Map<string, Blob>; manifest: ExportBundleManifest }> {
  const service = new ExportEngineService(storage || defaultStorageService);
  return service.prepareR2Payload(trip, photos, options);
}
